/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { installPersistedDocumentConsoleApi } from "./io/documentPersistence";
import { prepareWasmMeshKernels } from "./renderer/scene/tessellation/curvaTessWasm";

// Install before React mounts so recovery helpers work even if the editor crashes.
installPersistedDocumentConsoleApi();

// Warm tessellation Wasm early so the first canvas frame can use it.
void prepareWasmMeshKernels();

function isResizeObserverLoopError(value: unknown): boolean {
  const message = typeof value === "string"
    ? value
    : value instanceof Error
      ? value.message
      : undefined;
  return message === "ResizeObserver loop completed with undelivered notifications."
    || message === "ResizeObserver loop limit exceeded";
}

window.addEventListener("error", event => {
  if (isResizeObserverLoopError(event.message) || isResizeObserverLoopError(event.error)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

window.addEventListener("unhandledrejection", event => {
  if (isResizeObserverLoopError(event.reason)) {
    event.preventDefault();
  }
});

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

// https://bun.com/docs/bundler/hot-reloading#import-meta-hot-data
(import.meta.hot.data.root ??= createRoot(elem)).render(app);
